import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
  Chip,
  Alert,
  Fab,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment,
  PersonAdd,
  LocalHospital,
  Medication,
  Receipt,
  Menu as MenuIcon,
  AutoAwesome,
  Science,
  Settings as SettingsIcon,
  WifiOff,
  Wifi,
  Close,
  RateReview,
} from '@mui/icons-material';
import Dashboard from './components/Dashboard/Dashboard';
import Consultation from './components/Consultation/Consultation';
import Prescription from './components/Prescription/Prescription';
import RegistrationPage from './components/Registration/RegistrationPage';
import PreConsultationPage from './components/PreConsultation/PreConsultationPage';
import BillingPage from './components/Billing/BillingPage';
import AdvancedFeatures from './components/AdvancedFeatures/AdvancedFeatures';
import Dhis2Admin from './components/Dhis2Admin/Dhis2Admin';
import TestDhis2 from './components/TestDhis2/TestDhis2';
import AdminShell from './admin/AdminShell';
import RuntimeFormPage from './runtime/RuntimeFormPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserProvider, useUser } from './contexts/UserContext';
import { UiSettingsProvider, useUiSettings } from './contexts/UiSettingsContext';
import { SessionProvider, useSession } from './contexts/SessionContext';
import { BusinessProvider } from './contexts/BusinessContext';
import ContextSelector from './components/ContextSelector/ContextSelector';
import { MainTabProvider } from './contexts/MainTabContext';
import { FormDataProvider, useFormData } from './contexts/FormDataContext';
import { TranscriptProvider } from './contexts/TranscriptContext';
import { AmbientListeningProvider } from './contexts/AmbientListeningContext';
import { LabReportUploadProvider } from './contexts/LabReportUploadContext';
import { PrescriptionUploadProvider } from './contexts/PrescriptionUploadContext';
import { useOfflineMode } from './hooks/useOfflineMode';
import { ConfidenceToggleProvider } from './contexts/ConfidenceToggleContext';
import { StartSessionRequestProvider } from './contexts/StartSessionRequestContext';
import AudioSourceDialog, { AudioSourceType } from './components/Consultation/AudioSourceDialog';
import { FeatureAccessProvider } from './contexts/FeatureAccessContext';
import { SenseiChatProvider } from './contexts/SenseiChatContext';
import { PatientSummaryProvider } from './contexts/PatientSummaryContext';
import LoginPage from './components/Login/LoginPage';
import AutoLogoutManager from './components/AutoLogout/AutoLogoutManager';
import IdleLogoutCleanup from './components/AutoLogout/IdleLogoutCleanup';
import ExtractionFeedbackPanel from './components/ExtractionFeedback/ExtractionFeedbackPanel';
import './App.css';

function navigateTo(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// ─── Network Banner ───────────────────────────────────────────────────────────
//
// RENDERED VIA REACT PORTAL into document.body.
// This is mandatory: .app-root has overflow:hidden and MUI AppBar internally
// uses CSS transforms. Either of these alone can turn an ancestor into a
// "containing block" for position:fixed children, clipping the banner inside
// a 64px tall box that's invisible against the AppBar's own background.
// A portal places the DOM node directly under <body> — no ancestor can clip it.
//
// TOP OFFSET STRATEGY — getBoundingClientRect().bottom + requestAnimationFrame:
// - ResizeObserver.contentRect.height fires with 0 during DevTools open/close
//   transitions, causing top:0 which hides the banner behind the AppBar.
// - getBoundingClientRect().bottom returns the AppBar's actual distance from
//   the viewport top AFTER layout, and is never 0 while the bar is on screen.
// - We wrap every measurement in requestAnimationFrame so we always read the
//   value after the browser has committed the new layout geometry.

interface NetworkBannerProps {
  isOnline: boolean;
  isSlow: boolean;
  appBarRef: React.RefObject<HTMLElement | null>;
}

function NetworkBanner({ isOnline, isSlow, appBarRef }: NetworkBannerProps) {
  const [topOffset, setTopOffset] = useState(64);

  const measure = () => {
    requestAnimationFrame(() => {
      const el = appBarRef.current;
      if (!el) return;
      const bottom = el.getBoundingClientRect().bottom;
      if (bottom > 4) setTopOffset(bottom);
    });
  };

  useEffect(() => {
    measure();

    const el = appBarRef.current;
    if (!el) return;

    const ro = new ResizeObserver(measure);
    ro.observe(el);

    window.addEventListener('resize', measure);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appBarRef]);

  useEffect(() => {
    if (!isOnline || isSlow) measure();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, isSlow]);

  if (isOnline && !isSlow) return null;

  const banner = (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: topOffset,
        left: 0,
        right: 0,
        zIndex: 2147483647,
        backgroundColor: '#ffb74d',
        color: '#ffffff',
        textAlign: 'center',
        padding: '8px 16px',
        fontSize: '13.5px',
        fontWeight: 500,
        letterSpacing: '0.3px',
        boxSizing: 'border-box',
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        animation: 'networkBannerSlideIn 180ms ease-out',
      }}
    >
      {!isOnline
        ? 'No internet connection. Switch to a stable network and resume the session.'
        : 'Your network connection is unstable.'}
    </div>
  );

  return ReactDOM.createPortal(banner, document.body);
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`main-tabpanel-${index}`}
      aria-labelledby={`main-tab-${index}`}
      {...other}
      // style={{ height: '100%', minHeight: 0, display: value === index ? 'flex' : 'none', flexDirection: 'column' }      
      // IMPORTANT: flex-based sizing so the tab panel gets a bounded height.
      // This allows the right panel's internal `overflow: auto` to work instead of
      // the whole page scrolling.
      style={{
        flex: '1 1 0%',
        minHeight: 0,
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Prevent outer page scroll; left/right panels should manage their own internal scrolling. */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{children}</Box>
    </div>
  );
}

interface PatientRow {
  patient_id: number | string;
  name: string;
  mrn: string;
  age?: number;
  last_visit?: string;
  status: string;
  diagnosis?: string;
}

interface AppointmentRow {
  id: string | number;
  time: string;
  patient_name: string;
  mrn: string;
  patient_id?: number;
  provider?: string;
  type: string;
}

function AppContent() {
  const [activeTab, setActiveTab] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [overlayView, setOverlayView] = useState<'advanced' | 'testdhis2' | 'settings' | null>(null);
  /** When false, only Dashboard tab is visible. Set true when doctor opens a patient, starts an appointment, or adds a new patient. */
  const [workflowEntered, setWorkflowEntered] = useState(false);
  const { isOnline, isSlow, pendingActions } = useOfflineMode();
  const { doctor, setPatient, patient } = useUser();
  const { user, logout, access } = useAuth();
  const { showTestDhis2Tab } = useUiSettings();
  const showSettingsMenu = access == null || access.can_open_settings !== false;
  const showAdminPortal = access == null || access.can_open_admin_portal !== false;
  const showTestDhis2 =
    showTestDhis2Tab && (access == null || access.can_open_test_dhis2 !== false);
  const { session, clearSession, isEditingHistoricalForms, endFormSessionEdit } = useSession();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [audioSourceDialogOpen, setAudioSourceDialogOpen] = useState(false);
  const { clearFormForTab } = useFormData();

  // Passed to NetworkBanner so it can read the AppBar's live bottom edge
  const appBarRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const handler = () => setAudioSourceDialogOpen(true);
    window.addEventListener('open-audio-source-dialog', handler);
    return () => window.removeEventListener('open-audio-source-dialog', handler);
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const tab = visibleTabs[newValue];
    if (tab && isPendingNewRegistration && tabsLockedBeforeRegistration.includes(tab.id)) {
      return;
    }
    setActiveTab(newValue);
  };

  const handleAddNewPatient = () => {
    setWorkflowEntered(true);
    setPatient(null);
    clearSession();
    // When there is no active session, form state lives in memory (FormDataContext) and must be
    // cleared explicitly to avoid leaking defaults (e.g. arrival_time) across patients.
    clearFormForTab('Registration');
    clearFormForTab('PreConsultation');
    clearFormForTab('Consultation');
    clearFormForTab('Prescription');
    clearFormForTab('Billing');
    setActiveTab(1);
  };

  const handleOpenPatient = (row: PatientRow) => {
    setWorkflowEntered(true);
    // End any active session so we don't show previous patient's session/visit; form data clears when session is null
    clearSession();
    // If there is no session for the newly opened patient yet, the form context would otherwise retain
    // in-memory manual values from the previously opened patient (notably PreConsultation.arrival_time).
    clearFormForTab('Registration');
    clearFormForTab('PreConsultation');
    clearFormForTab('Consultation');
    clearFormForTab('Prescription');
    clearFormForTab('Billing');
    setPatient({
      patient_id: Number(row.patient_id),
      name: row.name,
      mrn: row.mrn,
      age: row.age ?? 0,
      gender: '',
      date_of_birth: null,
      contact_number: '',
    });
    // Selecting a patient from Dashboard should take you to Pre-Consultation.
    setActiveTab(2);
  };

  const handleStartConsultation = (apt: AppointmentRow) => {
    setWorkflowEntered(true);
    clearSession();
    const patientId = apt.patient_id ?? 0;
    setPatient({
      patient_id: patientId,
      name: apt.patient_name,
      mrn: apt.mrn,
      age: 0,
      gender: '',
      date_of_birth: null,
      contact_number: '',
    });
    setAudioSourceDialogOpen(true);
  };

  const handleAudioSourceConfirm = (audioSource: AudioSourceType) => {
    setAudioSourceDialogOpen(false);
    setActiveTab(3);
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('create-session-with-audio', { detail: { audioSource } }));
    }, 150);
  };

  const handleStartVisitFromRegistry = (row: PatientRow) => {
    handleStartConsultation({
      id: row.patient_id,
      time: '',
      patient_name: row.name,
      mrn: row.mrn,
      patient_id: Number(row.patient_id),
      type: 'Routine Visit',
    });
  };

  const mainTabs = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <DashboardIcon />,
      component: (
        <Dashboard
          onNavigateToTab={setActiveTab}
          onAddNewPatient={handleAddNewPatient}
          onOpenPatient={handleOpenPatient}
          onStartConsultation={handleStartConsultation}
          onStartVisitFromRegistry={handleStartVisitFromRegistry}
          isActive={activeTab === 0}
        />
      ),
    },
    { id: 'registration', label: 'Registration', icon: <PersonAdd />, component: <RegistrationPage /> },
    { id: 'preconsultation', label: 'Pre-Consultation', icon: <Assignment />, component: <PreConsultationPage /> },
    { id: 'patientvisit', label: 'Patient Visit', icon: <LocalHospital />, component: <Consultation /> },
    { id: 'prescription', label: 'Management', icon: <Medication />, component: <Prescription /> },
    { id: 'billing', label: 'Billing', icon: <Receipt />, component: <BillingPage /> },
  ];

  const tabsLockedBeforeRegistration = ['preconsultation', 'patientvisit', 'prescription', 'billing'];
  const isPendingNewRegistration = workflowEntered && !patient;

  const visibleTabsBase = workflowEntered ? mainTabs : mainTabs.filter((t) => t.id === 'dashboard');
  const allowedTabIds = access?.allowed_main_tabs;
  const visibleTabs =
    allowedTabIds && allowedTabIds.length > 0
      ? visibleTabsBase.filter((t) => allowedTabIds.includes(t.id))
      : visibleTabsBase;

  useEffect(() => {
    if (activeTab >= visibleTabs.length) setActiveTab(Math.max(0, visibleTabs.length - 1));
  }, [visibleTabs.length, activeTab]);

  const displayName = user?.displayName || user?.username || doctor?.name || 'Doctor';

  return (
    <MainTabProvider
      navigateToTabIndex={(index) => {
        setWorkflowEntered(true);
        setActiveTab(index);
      }}
      ensureWorkflowEntered={() => setWorkflowEntered(true)}
    >
      {/* Portal-rendered banner — lives in document.body, never clipped */}
      <NetworkBanner isOnline={isOnline} isSlow={isSlow} appBarRef={appBarRef} />

    <Box className="app-root" sx={{ flexGrow: 1, minHeight: 0, bgcolor: '#f5f5f5' }}>
      <AppBar position="static" className="app-header" sx={{ bgcolor: '#012676', overflow: 'hidden' }} ref={appBarRef}>
        <Toolbar
          sx={{
            px: { xs: 1, sm: 2 },
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
            rowGap: 1,
            columnGap: 0.5,
            alignItems: 'center',
            minHeight: { xs: 'auto !important', sm: '56px !important', md: '64px !important' },
            py: { xs: 1, lg: 0.5 },
            boxSizing: 'border-box',
            maxWidth: '100%',
          }}
        >
          {/* Row 1 (xs): brand + actions. Full row (lg): brand first. */}
          <Box
            sx={{
              order: 1,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 },
              flex: { xs: '1 1 auto', lg: '0 0 auto' },
              minWidth: 0,
            }}
          >
            <Box
              component="img"
              src="/assets/intellicare_icon.png"
              alt="Intellicare"
              sx={{ height: { xs: 26, sm: 34 }, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
            />
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 600,
                fontSize: { xs: '0.9rem', sm: '1.2rem' },
                color: 'white',
                minWidth: 0,
              }}
            >
              Intellicare
            </Typography>
          </Box>

          {/* Desktop: pushes context + user cluster to the right. Hidden when header wraps (mobile). */}
          <Box
            sx={{
              display: { xs: 'none', lg: 'block' },
              flex: '1 1 0%',
              minWidth: 0,
              order: 2,
            }}
          />

          {/* Row 2 (xs): full-width context. (lg): right side next to profile / sign out. */}
          <Box
            sx={{
              order: { xs: 3, lg: 3 },
              flex: { xs: '1 1 100%', lg: '0 1 auto' },
              minWidth: 0,
              maxWidth: { xs: '100%', lg: 'none' },
              width: { xs: '100%', lg: 'auto' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'stretch', lg: 'flex-end' },
              ml: { xs: 0, lg: 0 },
            }}
          >
            <ContextSelector />
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 0.25, sm: 0.5 }}
            sx={{
              order: { xs: 2, lg: 4 },
              flexShrink: 0,
              ml: { xs: 'auto', lg: 0 },
              pl: { xs: 0, lg: 1 },
            }}
          >
            <Typography variant="body2" sx={{ color: 'white', display: { xs: 'none', md: 'block' }, maxWidth: 120 }} noWrap title={displayName}>
              {displayName}
            </Typography>
            <Box
              component="img"
              src="/assets/Admin Settings Male.png"
              alt=""
              sx={{
                height: { xs: 28, sm: 32 },
                width: { xs: 28, sm: 32 },
                borderRadius: '50%',
                objectFit: 'cover',
                display: { xs: 'none', sm: 'block' },
              }}
            />
            <Tooltip title="Sign out">
              <Button
                size="small"
                onClick={logout}
                sx={{
                  color: 'white',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  minWidth: { xs: 36, sm: 'auto' },
                  px: { xs: 0.5, sm: 1 },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
                startIcon={<Box component="img" src="/assets/Logout.png" alt="" sx={{ height: 18, width: 18 }} />}
              >
                Sign Out
              </Button>
            </Tooltip>
            <Tooltip title="Sign out">
              <IconButton
                size="small"
                onClick={logout}
                sx={{ color: 'white', display: { xs: 'inline-flex', sm: 'none' } }}
                aria-label="Sign out"
              >
                <Box component="img" src="/assets/Logout.png" alt="" sx={{ height: 20, width: 20 }} />
              </IconButton>
            </Tooltip>

            <IconButton
              size="medium"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ color: 'white' }}
              aria-label="Menu"
            >
              <MenuIcon />
            </IconButton>

            <Chip
              icon={isOnline ? <Wifi /> : <WifiOff />}
              label={
                isOnline
                  ? isSlow
                    ? 'Slow'
                    : 'Online'
                  : `Offline${pendingActions.length > 0 ? ` (${pendingActions.length})` : ''}`
              }
              size="small"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                bgcolor: isOnline ? (isSlow ? '#e65100' : '#009688') : '#c62828',
                color: 'white',
                fontWeight: 500,
                fontSize: '0.75rem',
                maxWidth: { sm: 140 },
                '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          </Stack>
        </Toolbar>
      </AppBar>

      {isEditingHistoricalForms && (
        <Alert
          severity="info"
          onClose={endFormSessionEdit}
          sx={{ borderRadius: 0, py: 0.5 }}
          action={
            <Button color="inherit" size="small" onClick={endFormSessionEdit} sx={{ fontWeight: 600 }}>
              Done editing
            </Button>
          }
        >
          You are editing forms for a past visit. All main tabs (Registration through Billing) use that session&apos;s data. The microphone is disabled until you finish.
        </Alert>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setOverlayView('advanced');
          }}
        >
          <AutoAwesome sx={{ mr: 1.5, fontSize: 20 }} /> Advanced Features
        </MenuItem>
        {showTestDhis2 && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setOverlayView('testdhis2');
            }}
          >
            <Science sx={{ mr: 1.5, fontSize: 20 }} /> Test DHIS2
          </MenuItem>
        )}
        {showAdminPortal && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              navigateTo('/admin');
            }}
          >
            <SettingsIcon sx={{ mr: 1.5, fontSize: 20 }} /> Admin Portal
          </MenuItem>
        )}
        {showSettingsMenu && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              setOverlayView('settings');
            }}
          >
            <SettingsIcon sx={{ mr: 1.5, fontSize: 20 }} /> Settings
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={overlayView !== null}
        onClose={() => setOverlayView(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '95vh',
            '@supports (height: 100dvh)': { maxHeight: '95dvh' },
          },
        }}
      >
        <DialogContent sx={{ p: 0, overflow: 'auto' }}>
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
            <IconButton onClick={() => setOverlayView(null)} size="small">
              <Close />
            </IconButton>
          </Box>
          <Box sx={{ pt: 4, px: 2, pb: 2 }}>
            {overlayView === 'advanced' && <AdvancedFeatures />}
            {overlayView === 'testdhis2' && <TestDhis2 />}
            {overlayView === 'settings' && <Dhis2Admin />}
          </Box>
        </DialogContent>
      </Dialog>

      <Box
        className="app-tabs-wrap"
        sx={{
          borderBottom: 1,
          borderColor: '#e0e0e0',
          bgcolor: '#fff',
          px: { xs: 0.5, sm: 1 },
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              '& .MuiTabs-scroller': { overflowY: 'hidden' },
              '& .MuiTab-root': {
                minHeight: 48,
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                fontWeight: 500,
                textTransform: 'none',
                px: { xs: 1, sm: 1.5 },
                minWidth: { xs: 'auto', sm: 90 },
              },
              '& .Mui-selected': { fontWeight: 600 },
              '& .MuiTabs-indicator': { bgcolor: '#012676', height: 3 },
            }}
          >
            {visibleTabs.map((tab, index) => {
              const isDashboard = tab.id === 'dashboard';
              const disabledByActiveSession = isDashboard && session?.status === 'active';
              const disabledByRegistration =
                isPendingNewRegistration && tabsLockedBeforeRegistration.includes(tab.id);
              const disabled = disabledByActiveSession || disabledByRegistration;
              const disabledTitle = disabledByRegistration
                ? 'Complete registration first'
                : disabledByActiveSession
                  ? 'Finish or end the active visit to return to Dashboard'
                  : '';
              const tabEl = (
                <Tab
                  key={tab.id}
                  icon={tab.icon}
                  iconPosition="start"
                  label={tab.label}
                  id={`main-tab-${index}`}
                  disabled={disabled}
                  sx={disabled ? { opacity: 0.5 } : {}}
                />
              );
              return disabledTitle ? (
                <Tooltip key={tab.id} title={disabledTitle}>
                  <span>{tabEl}</span>
                </Tooltip>
              ) : (
                tabEl
              );
            })}
          </Tabs>
      </Box>

      <Box className="app-content-wrap" sx={{ px: { xs: 0.5, sm: 1 }, flex: 1, minHeight: 0 }}>
          {visibleTabs.map((tab, index) => (
            <TabPanel key={tab.id} value={activeTab} index={index}>
              {tab.component}
            </TabPanel>
          ))}
      </Box>

      <Box className="app-footer" sx={{ py: 0.75, px: 2, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
          Copyright © Dure Technologies Pvt. Ltd.
        </Typography>
      </Box>
      <Fab
        color="primary"
        onClick={() => setFeedbackOpen(true)}
        sx={{
          position: 'fixed',
          right: 20,
          bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
          bgcolor: '#012676',
          '&:hover': { bgcolor: '#001f5c' },
        }}
        aria-label="AI extraction feedback"
      >
        <RateReview />
      </Fab>
      <ExtractionFeedbackPanel open={feedbackOpen} onClose={() => setFeedbackOpen(false)} sessionId={session?.session_id ?? null} />
      <AudioSourceDialog
        open={audioSourceDialogOpen}
        onClose={() => setAudioSourceDialogOpen(false)}
        onSelect={handleAudioSourceConfirm}
      />
    </Box>
    </MainTabProvider>
  );
}

function AppGate() {
  const { isAuthenticated } = useAuth();
  const [pathname, setPathname] = useState(window.location.pathname);
  useEffect(() => {
    const handler = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  if (!isAuthenticated) return <LoginPage />;
  const isAdminRoute = pathname.startsWith('/admin');
  const isRuntimeFormRoute = pathname.startsWith('/runtime-form/');
  return (
    <AutoLogoutManager>
    <BusinessProvider>
      {isRuntimeFormRoute ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <RuntimeFormPage />
        </Box>
      ) : isAdminRoute ? (
        <AdminShell />
      ) : (
        <UserProvider>
          <SessionProvider>
            <PatientSummaryProvider>
            <SenseiChatProvider>
              <FormDataProvider>
                <StartSessionRequestProvider>
                <ConfidenceToggleProvider>
                  <TranscriptProvider>
                    <AmbientListeningProvider>
                      <IdleLogoutCleanup />
                    <LabReportUploadProvider>
                      <PrescriptionUploadProvider>
                        <UiSettingsProvider>
                          <FeatureAccessProvider>
                            <AppContent />
                          </FeatureAccessProvider>
                        </UiSettingsProvider>
                      </PrescriptionUploadProvider>
                    </LabReportUploadProvider>
                    </AmbientListeningProvider>
                  </TranscriptProvider>
                </ConfidenceToggleProvider>
                </StartSessionRequestProvider>
              </FormDataProvider>
            </SenseiChatProvider>
            </PatientSummaryProvider>
          </SessionProvider>
        </UserProvider>
      )}
    </BusinessProvider>
    </AutoLogoutManager>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppGate />
    </AuthProvider>
  );
}

export default App;
